import clsx from 'clsx';
import ItemComplexList from 'core/modules/components/general/ItemComplexList';
import ProgramImage from 'core/modules/components/general/ProgramImage/ProgramImage';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from 'ui';

const CalendarItem = ({
  iconType,
  getVisibilityLabel,
  gradient,
  name,
  showGroupName,
  school_group_name,
  visible,
  guid,
  onClickVisible,
  showOptions,
  onConfigureCourse,
  configProgramPicture,
  course_picture,
  calendarTheme,
  themeColor,
  themeImage,
}) => {
  const { t } = useTranslation();

  // The consumer can name the toggle after what its own panel filters ("Ver tareas de X"). The
  // default is the course name itself: always present, so a consumer that forgets the prop never
  // leaves the control unnamed, and it carries no concept noun — Edelvives renames "course" to
  // "grupo"/"libro" and uses "Curso" for the school year, so any noun here would contradict the
  // gear right next to it.
  const visibilityLabel = getVisibilityLabel ? getVisibilityLabel(name) : name;

  function handleClickVisible() {
    onClickVisible(guid);
  }

  // A native checkbox only toggles with Space, and the criteria ask for Enter too. `repeat` guards
  // against the OS key autorepeat sending one toggle per keydown while Enter is held down.
  function handleVisibleKeyDown(event) {
    if (event.key !== 'Enter' || event.repeat) return;
    event.preventDefault();
    onClickVisible(guid);
  }

  function renderLeft() {
    return <div className="calendar-item__course-color" style={{ backgroundColor: themeColor }}></div>;
  }

  function renderCenter() {
    return (
      <div className={clsx('calendar-item__info', { 'calendar-item__info--invisible': !visible })}>
        <ProgramImage
          configProgramPicture={configProgramPicture}
          calendarTheme={calendarTheme}
          iconType={iconType}
          gradient={themeColor ? themeColor : gradient}
          course_picture={course_picture}
          themeImage={themeImage}
        />

        <div className="calendar-item__text-wrapper">
          <div className="calendar-item__name" title={name}>
            {name}
          </div>
          {showGroupName && (
            <div className="calendar-item__level" title={school_group_name}>
              {school_group_name}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="calendar-item">
      <ItemComplexList
        left={renderLeft()}
        center={renderCenter()}
        right={
          <div className="calendar-item__options-wrapper">
            {/* A native checkbox, not a button with `aria-pressed`: VoiceOver announces the
                unpressed state of a toggle button as nothing at all, so the "hidden" state was
                unreachable for a screen reader. The state now comes from the control itself.
                Same composition the design system uses for its own Switch: the input carries the
                semantics and is `sr-only`, the icon is the visual. */}
            <label className="calendar-item__visible-icon">
              <input
                aria-label={visibilityLabel}
                checked={visible}
                className="sr-only"
                onChange={handleClickVisible}
                onKeyDown={handleVisibleKeyDown}
                type="checkbox"
              />
              <Icon size="md" name={visible ? 'visible' : 'visible-off'} />
            </label>
            {showOptions && (
              <button
                aria-label={`${t('course:Configure course')}: ${name}`}
                className="button-accessibility-only calendar-item__options"
                onClick={onConfigureCourse}
                type="button"
              >
                <Icon name="settings" size="md" />
              </button>
            )}
          </div>
        }
      />
    </div>
  );
};

CalendarItem.propTypes = {
  calendarTheme: PropTypes.shape({ color: PropTypes.string }),
  configProgramPicture: PropTypes.bool,
  course_picture: PropTypes.string,
  getVisibilityLabel: PropTypes.func,
  gradient: PropTypes.string,
  guid: PropTypes.string,
  iconType: PropTypes.string,
  name: PropTypes.string,
  onClickVisible: PropTypes.func,
  onConfigureCourse: PropTypes.func,
  school_group_name: PropTypes.string,
  showGroupName: PropTypes.bool,
  showOptions: PropTypes.bool,
  themeColor: PropTypes.string,
  themeImage: PropTypes.string,
  visible: PropTypes.bool,
};

CalendarItem.defaultProps = {
  visible: true,
  onClickVisible: () => {},
};

export default CalendarItem;
